/**
 * Represents a Directed Acyclic Graph (DAG).
 * 
 * Provides methods for adding, deleting edges, 
 * counting paths, finding reachable nodes, and graph traversal.
 */
export class DAG {
    graph;
    no_of_vertex;

    /**
     * Creates a new Directed Acyclic Graph.
     * @param number - Total number of vertices in the graph.
     */
    constructor(number) {
        this.no_of_vertex = number;
        this.graph = {};
    }

    /**
     * Adds a new edge between parent and child in the provided graph object.
     * @param graph - Graph object in adjacency list representation.
     * @param parent - Parent node.
     * @param child - Child node.
     * @returns Updated graph object.
     */
    addNewEdgeInGraph(graph, parent, child) {
        if (!graph[parent]) {
            graph[parent] = [];
        }
        graph[parent].push(child);
        return graph;
    }

    /**
     * Adds a new edge between parent and child in the class graph.
     * @param parent - Parent node.
     * @param child - Child node.
     */
    addEdge(parent, child) {
        if (!this.graph[parent]) {
            this.graph[parent] = [];
        }
        this.graph[parent].push(child);
    }

    /**
     * Deletes an edge between two nodes in the class graph.
     * Removes both directions if present.
     * @param first_node - The starting node.
     * @param second_node - The ending node.
     */
    deleteEdge(first_node, second_node) {
        /* Traversing through the first vector list and removing the second element from it */
        if (this.graph[first_node]) {
            let index = this.graph[first_node].findIndex(ob => ob === second_node);
            if (index !== -1) {
                this.graph[first_node].splice(index, 1);
            }

            if (this.graph[first_node].length === 0) {
                delete this.graph[first_node];
            }
        }
        /* Traversing through the first vector list and removing the second element from it */
        if (this.graph[second_node]) {
            let index = this.graph[second_node].findIndex(ob => ob === first_node);
            if (index !== -1) {
                this.graph[second_node].splice(index, 1);
            }

            if (this.graph[second_node].length === 0) {
                delete this.graph[second_node];
            }
        }
    }

    /**
     * Deletes an edge between two nodes in the given graph object.
     * Removes both directions if present.
     * @param graph - Graph object in adjacency list representation.
     * @param first_node - The starting node.
     * @param second_node - The ending node.
     * @returns Updated graph object.
     */
    deleteEdgeInGraph(graph, first_node, second_node) {
        /* Traversing through the first vector list and removing the second element from it */
        if (graph[first_node]) {
            let index = graph[first_node].findIndex(ob => ob === second_node);
            if (index !== -1) {
                graph[first_node].splice(index, 1);
            }
            if (graph[first_node].length === 0) {
                delete graph[first_node];
            }
        }
        /* Traversing through the first vector list and removing the second element from it */
        if (graph[second_node]) {
            let index = graph[second_node].findIndex(ob => ob === first_node);
            if (index !== -1) {
                graph[second_node].splice(index, 1);
            }
            if (graph[second_node].length === 0) {
                delete graph[second_node];
            }
        }
        return graph;
    }


    /**
     * Counts the number of distinct paths from source to destination.
     * @param graph - Graph object in adjacency list representation.
     * @param source - Source node.
     * @param destination - Destination node.
     * @returns Number of possible paths.
     */
    countPaths(graph, source: number, destination: number) {
        /* Call the recursive method to count all paths */
        let path_count = 0;
        path_count = this.countPathsUtil(graph, source, destination, path_count);
        return path_count;
    }

    /**
     * Recursive utility to count paths from source to destination.
     * @param graph - Graph object.
     * @param source - Source node.
     * @param destination - Destination node.
     * @param path_count - Current count of paths.
     * @returns Updated path count.
     */
    countPathsUtil(graph, source, destination, path_count) {
        /*  If current vertex is same as destination, then increment count */
        if (graph[source]) {
            path_count = this.countPath(graph, graph[source], destination, path_count);
        }

        if (graph[destination]) {
            path_count = this.countPath(graph, graph[destination], source, path_count);
        }
        return path_count;
    }

     /**
     * Helper function to count paths by traversing adjacency list.
     * @param graph - Graph object.
     * @param list - List of neighbors for current node.
     * @param node - Destination node.
     * @param path_count - Current count of paths.
     * @returns Updated path count.
     */
    countPath = (graph, list, node, path_count) => {
        if (list.includes(node)) {
            path_count++;
        }
        /* Recur for all the vertices adjacent to this vertex */
        else {
            for (let g_src of list) {
                path_count = this.countPathsUtil(graph, g_src, node, path_count);
            }
        }
        return path_count;
    }

    /**
     * Finds all reachable nodes for a given set of source nodes.
     * @param source_nodes - List of source nodes.
     * @returns Map of source node → list of reachable nodes.
     */
    findReachableNodes = (source_nodes: number[]) => {
        // Take a integer visited array and initialize
        // all the elements with 0
        let visited: number[] = [];

        // Map to store list of reachable Nodes for a
        // given node.
        let reachable_nodes = {};

        // Initialize component Number with 0
        let component_num = 0;

        // For each node in source_nodes[] find reachable Nodes
        for (let source of source_nodes) {
            // Visit all the nodes of the component
            if (!visited[source]) {
                component_num++;
                // Store the reachable Nodes corresponding to source
                reachable_nodes[source] = this.graphTraversalBFS(component_num, source, visited);
            }
        }
        return reachable_nodes;
    }

    /**
     * Inserts a neighbor into the result if it is not the source and not already included.
     * @param result - Array of reachable nodes.
     * @param current_vertex - The node being processed.
     * @param source - Source node.
     */
    insertNeighbour = (result: number[], current_vertex: number, source: number) => {
        if (!result.includes(current_vertex) && current_vertex !== source) {
            result.push(current_vertex);
        }
    }

    /**
     * Marks a node as visited and adds it to the BFS queue.
     * @param node - Node to mark as visited.
     * @param component_num - Current connected component number.
     * @param visited - Array marking visited nodes.
     * @param queue - BFS traversal queue.
     */
    addVisitedNode = (node: string, component_num: number, visited: number[], queue: number[]) => {
        let node_as_number  = Number(node);
        if (!visited[node_as_number]) {
            visited[node_as_number] = component_num;
            queue.push(node_as_number);
        }
    }

    /**
     * Performs BFS traversal from a given source node.
     * @param component_num - Current connected component number.
     * @param source - Source node.
     * @param visited - Array marking visited nodes.
     * @returns List of reachable nodes.
     */
    graphTraversalBFS = (component_num: number, source: number, visited: number[]) => {
        let queue: number[] = [source];
        let result: number[] = [];
        visited[source] = component_num;
        while (queue.length) {
            let current_vertex = queue.shift();
            if (current_vertex) {
                this.insertNeighbour(result, current_vertex, source);
            }
            Object.entries(this.graph).forEach(([key, value], i) => {
                if (Number(key) === current_vertex) {
                    this.insertNeighbour(result, current_vertex, source);
                    this.addVisitedNode(key, component_num, visited, queue);

                    this.graph[current_vertex].forEach(neighbor => {
                        this.addVisitedNode(neighbor, component_num, visited, queue);
                    });

                }
                else if (this.graph[key].includes(current_vertex)) {
                    if (current_vertex){
                        this.insertNeighbour(result, current_vertex, source);
                    }
                    this.addVisitedNode(key, component_num, visited, queue);
                }
            });
        }
        return result;
    }

    /**
     * Displays reachable nodes in the console.
     * @param reachable_nodes - List of reachable nodes.
     */
    displayReachableNodes = (reachable_nodes: number[]) => {
        for (let node of reachable_nodes) {
            console.log(" ", node, " ");
        }
    }

    /**
     * Prints the graph to the console in adjacency list format.
     * @param graph - Graph object in adjacency list representation.
     */
    printGraph(graph) {
        Object.entries(graph).forEach(([key, value], i) => {
            console.log("vertex " + i + " ");
            graph[key].forEach(element => {
                console.log("-> " + element);
            });
        });
    }

}